import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import artworksData from '../data/artworks.json';

const ArtContainer = styled.div`
  padding: 2rem;
  margin-top: 60px;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 500;
  margin: 20px 0 30px;
  color: #111;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  padding: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
`;

const ArtworkThumbnail = styled(Link)`
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  padding-bottom: 70%; // Creates a 10:7 aspect ratio (1400/2000 = 0.7)
  overflow: hidden;
  margin-bottom: 1rem;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${ArtworkThumbnail}:hover & {
    transform: scale(1.05);
  }
`;

const Title = styled.h3`
  font-size: 1rem;
  margin: 0.5rem 0 0.25rem 0;
  color: #333;
`;

const Description = styled.div`
  font-size: 11px;
  font-weight: 300;
  color: #666;
  margin-top: 0.25rem;
`;

const FilterContainer = styled.div`
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-family: "Albert Sans", sans-serif;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: "Albert Sans", sans-serif;
  font-size: 0.9rem;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #999;
  }
`;

const Label = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const AllArt = () => {
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [filters, setFilters] = useState({
    series: '',
    search: ''
  });

  // Get unique values for filters
  const series = [...new Set(artworksData.map(art => art.series).filter(Boolean))];

  useEffect(() => {
    document.title = "Artworks - SLEEPR";
    // Start with a fresh copy of the data
    let filtered = [...artworksData];

    // Apply search filter first
    if (filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(art => 
        art.title.toLowerCase().includes(searchTerm)
      );
    }

    // Apply series filter
    if (filters.series) {
      filtered = filtered.filter(art => art.series === filters.series);
    }

    // Sort by year (newest first)
    filtered.sort((a, b) => parseInt(b.year) - parseInt(a.year));

    // Ensure no duplicates by using slug
    filtered = filtered.filter((art, index, self) =>
      index === self.findIndex((t) => t.slug === art.slug)
    );

    setFilteredArtworks(filtered);
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <ArtContainer>
      <PageTitle>Artworks</PageTitle>
      <FilterContainer>
        <FilterGroup>
          <Label>Filter:</Label>
          <Select 
            name="series" 
            value={filters.series}
            onChange={handleFilterChange}
          >
            <option value="">All Series</option>
            {series.map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <Label>Search:</Label>
          <SearchInput
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search titles..."
          />
        </FilterGroup>
      </FilterContainer>

      <Gallery>
        {filteredArtworks.map((artwork) => (
          <ArtworkThumbnail key={artwork.slug} to={`/art/${artwork.slug}`}>
            <ImageContainer>
              <Image src={`/img${artwork.image_small}.jpg`} alt={artwork.title} />
            </ImageContainer>
            <Title>{artwork.title}</Title>
            <Description>
              {artwork.year}. 1 of 1 original, {artwork.size}.
            </Description>
          </ArtworkThumbnail>
        ))}
      </Gallery>
    </ArtContainer>
  );
};

export default AllArt; 